import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import Question from './Question';
import { Step, StepContent, Stepper } from '@mui/material';
import StepIconLabel from './StepIconLabel';
import { getSurveyState } from '@act/shared/utilities';
import { surveysRequests } from 'redux/requests/surveys.requests';
import { makeRequest } from 'redux/utils/apiRequest';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { surveyActions } from 'redux/slices/surveys.slice';
import { selectSelectedMember } from 'redux/slices/memberSlice.slice';
import { SurveyTemplateParser } from '@act/storybook';

export interface SurveyStepperProps {
  submittingForm: boolean;
  assignedSurvey: SurveyV2.AssignedSurveyDto;
  onFormStateChange?: (isDirty: boolean) => void;
}

type StepsType = ReturnType<
  typeof getSurveyState<
    SurveyV2.QuestionDto,
    SurveyV2.CheckpointDto,
    SurveyV2.QuestionResponseDto
  >
>['steps'];

const SurveyStepper = ({
  assignedSurvey,
  submittingForm,
  onFormStateChange,
}: SurveyStepperProps) => {
  const dispatch = useAppDispatch();

  const selectedMember: UserDto = useAppSelector(selectSelectedMember);

  const [selectedStep, setSelectedStep] = useState<number>(null);
  const [dirtyQuestions, setDirtyQuestions] = useState<Set<number>>(new Set());

  const prevStepsRef = useRef<StepsType>();
  const steps = useMemo(() => {
    // If the form is being submitted don't update the steps
    if (submittingForm) {
      return prevStepsRef.current;
    }

    const { steps } = getSurveyState(
      assignedSurvey.questions,
      assignedSurvey.questionResponses,
      assignedSurvey.surveyVersion?.defaultFinalCheckpoint,
      assignedSurvey.phases
    );
    prevStepsRef.current = steps;

    return steps;
  }, [assignedSurvey, submittingForm]);

  async function handleChange(
    answer: SurveyV2.AnswerQuestionDto
  ): Promise<boolean> {
    try {
      const result = await makeRequest(surveysRequests.setQuestionResponse, {
        params: null,
        body: answer,
      });

      dispatch(surveyActions.upsertAssignedSurvey(result.assignedSurvey));

      return true;
    } catch (error) {
      return false;
    }
  }

  function handleStep(step: number): void {
    setSelectedStep(step);
  }

  const handleQuestionFormStateChange = useCallback(
    (questionId: number, isDirty: boolean) => {
      setDirtyQuestions(prev => {
        const newSet = new Set(prev);
        if (isDirty) {
          newSet.add(questionId);
        } else {
          newSet.delete(questionId);
        }
        return newSet;
      });
    },
    []
  );

  const hasAnyDirtyForm = dirtyQuestions.size > 0;
  useEffect(() => {
    onFormStateChange?.(hasAnyDirtyForm);
  }, [hasAnyDirtyForm, onFormStateChange]);

  // If activeStep hasn't been set explicitly then we should default to the last step.
  const activeStep = selectedStep === null ? steps.length - 1 : selectedStep;

  return (
    <Stepper nonLinear activeStep={activeStep} orientation="vertical">
      {steps?.map((step, index) => (
        <Step
          key={`${index}-${step.question?.id ?? 'q'}-${step.checkpoint?.id ?? 'c'}`}
        >
          <StepIconLabel
            title={
              step.stopAtCheckpoint
                ? step.checkpoint?.title
                : step.question?.title
            }
            isActive={activeStep === index}
            handleStep={() => handleStep(index)}
          />
          <StepContent sx={{ paddingTop: '24px' }}>
            {step.stopAtCheckpoint ? (
              <div>
                <SurveyTemplateParser
                  template={step.checkpoint.message}
                  member={selectedMember}
                  assignedSurvey={assignedSurvey}
                />
              </div>
            ) : (
              <QuestionWrapper
                question={step.question}
                assignedSurvey={assignedSurvey}
                member={selectedMember}
                questionResponse={step.response}
                activeStep={activeStep}
                numberOfSteps={steps.length}
                handleChange={handleChange}
                handleStep={handleStep}
                onFormStateChange={handleQuestionFormStateChange}
              />
            )}
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
};

interface QuestionWrapperProps {
  question: SurveyV2.QuestionDto;
  assignedSurvey: SurveyV2.AssignedSurveyDto;
  member: UserDto;
  questionResponse: SurveyV2.QuestionResponseDto;
  activeStep: number;
  numberOfSteps: number;
  handleChange: (answer: SurveyV2.AnswerQuestionDto) => Promise<boolean>;
  handleStep: (step: number) => void;
  onFormStateChange: (questionId: number, isDirty: boolean) => void;
}

const QuestionWrapper = ({
  question,
  assignedSurvey,
  member,
  questionResponse,
  activeStep,
  numberOfSteps,
  handleChange,
  handleStep,
  onFormStateChange,
}: QuestionWrapperProps) => {
  const handleQuestionChange = useCallback(
    (isDirty: boolean) => {
      onFormStateChange(question.id, isDirty);
    },
    [question.id, onFormStateChange]
  );

  return (
    <Question
      question={question}
      assignedSurvey={assignedSurvey}
      member={member}
      questionResponse={questionResponse}
      activeStep={activeStep}
      numberOfSteps={numberOfSteps}
      handleChange={handleChange}
      handleStep={handleStep}
      onFormStateChange={handleQuestionChange}
    />
  );
};

export default SurveyStepper;
